import { useMemo, useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CurrencySelect,
  FormField,
  Input,
  Textarea,
} from '@shared/ui';
import { ApiError, newIdempotencyKey } from '@shared/api';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { ALLOWED_CONTENT_TYPES } from '@entities/media';
import { MediaPicker } from '@features/media-picker';
import { useProfileQuery, isVendorVerified } from '@entities/manufacturer';
import { useSubmitQuoteMutation } from '@entities/rfq';
import { ROUTES } from '@shared/config/routes';
import { quoteFormSchema, emptyQuoteForm, formToRequest, type QuoteFormValues } from '../model/schema';

interface QuoteFormProps {
  buyRequestId: string;
}

export function QuoteForm({ buyRequestId }: QuoteFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile } = useProfileQuery();
  const verified = isVendorVerified(profile?.kycStatus);

  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const mutation = useSubmitQuoteMutation(buyRequestId, idempotencyKey);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const form = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: emptyQuoteForm(),
    mode: 'onBlur',
  });

  const onSubmit = (values: QuoteFormValues) => {
    setSubmitError(null);
    setConflict(false);
    mutation.mutate(formToRequest(values), {
      onSuccess: () => navigate(ROUTES.quotes),
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.status === 409) {
            setConflict(true);
            setSubmitError('You have already responded to this request.');
            return;
          }
          if (err.code === 'DOMAIN_RULE_VIOLATION' && /verified/i.test(err.message)) {
            setSubmitError(t('buyRequests.respond.needVerified'));
            return;
          }
        }
        if (!applyApiFieldErrors(err, form.setError)) {
          setSubmitError(err instanceof ApiError ? err.message : 'Submission failed');
        }
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
      {!verified ? (
        <Alert type="warning" showIcon message={t('buyRequests.respond.kycRequired')} />
      ) : null}

      <Card>
        <CardContent className="grid gap-5 pt-6 md:grid-cols-2">
          <FormField
            label={t('buyRequests.respond.fields.price')}
            required
            error={form.formState.errors.priceAmount?.message}
          >
            {(p) => (
              <div className="flex gap-2">
                <Input
                  {...p}
                  inputMode="decimal"
                  placeholder="0.00"
                  className="flex-1"
                  {...form.register('priceAmount')}
                />
                <Controller
                  control={form.control}
                  name="priceCurrency"
                  render={({ field }) => (
                    <CurrencySelect value={field.value} onChange={field.onChange} />
                  )}
                />
              </div>
            )}
          </FormField>

          <FormField
            label={t('buyRequests.respond.fields.leadTime')}
            required
            error={form.formState.errors.leadTimeDays?.message}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                {...form.register('leadTimeDays', { valueAsNumber: true })}
              />
            )}
          </FormField>

          <FormField
            label={t('buyRequests.respond.fields.terms')}
            hint={t('buyRequests.respond.fields.termsHint')}
            error={form.formState.errors.terms?.message}
            className="md:col-span-2"
          >
            {(p) => <Textarea {...p} rows={6} {...form.register('terms')} />}
          </FormField>

          <div className="md:col-span-2">
            <Controller
              control={form.control}
              name="attachments"
              render={({ field }) => (
                <MediaPicker
                  scope="QUOTE_ATTACHMENT"
                  label={t('buyRequests.respond.fields.attachments')}
                  hint={t('buyRequests.respond.fields.attachmentsHint')}
                  accept={[...ALLOWED_CONTENT_TYPES.image, ...ALLOWED_CONTENT_TYPES.document]}
                  multiple
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {submitError ? (
        <Alert
          type="error"
          showIcon
          message={submitError}
          action={
            conflict ? (
              <Button type="button" size="sm" variant="outline" onClick={() => navigate(ROUTES.quotes)}>
                {t('buyRequests.respond.goToMyQuotes')}
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <div className="flex justify-between gap-3">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={mutation.isPending} disabled={!verified}>
          {t('buyRequests.respond.submit')}
        </Button>
      </div>
    </form>
      </FormProvider>
  );
}
